import './LoginForm.css'
import {useState, useEffect, useRef} from 'react';
import {useNavigate} from "react-router-dom";
import { Globals } from '../globalfunctions.js';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import TwoFactorAuth from '../components/TwoFactorAuth';
import simpleSessionTimer from '../utils/simpleSessionTimer';
import { apiClient } from '../utils/apiClient';

const RegisterForm = () => {
    
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [first_name, setFirstName] = useState('');
    const [last_name, setLastName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [postcode, setPostcode] = useState('');
    const [country, setCountry] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(null);
    const [errors, setErrors] = useState({});
    const [showMandatory2FA, setShowMandatory2FA] = useState(false);
    const [registrationData, setRegistrationData] = useState(null);
    const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' });
    const usernameCheckTimeout = useRef(null);
    
    const validateForm = () => {
        const newErrors = {};
        
        if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        if (!passwordStrength || passwordStrength.level === 'weak') {
            newErrors.password = 'Password is too weak. Please choose a stronger password.';
        }
        
        if (first_name.trim().length < 2) {
            newErrors.first_name = 'First name must be at least 2 characters';
        }
        
        if (last_name.trim().length < 2) {
            newErrors.last_name = 'Last name must be at least 2 characters';
        }
        
        if (username.trim().length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        }
        
        if (usernameStatus.available === false) {
            newErrors.username = 'Username is already taken';
        }
        
        if (usernameStatus.available === false) {
            newErrors.username = 'Username is already taken';
        }
        
        if (usernameStatus.checking) {
            newErrors.username = 'Checking username availability...';
        }
        
        if (companyName.trim().length < 2) {
            newErrors.companyName = 'Company name must be at least 2 characters';
        }
        
        if (address1.trim().length < 5) {
            newErrors.address1 = 'Address line 1 must be at least 5 characters';
        }
        
        if (city.trim().length < 2) {
            newErrors.city = 'City must be at least 2 characters';
        }
        
        if (postcode.trim().length < 3) {
            newErrors.postcode = 'Postcode must be at least 3 characters';
        }
        
        if (country.trim().length < 2) {
            newErrors.country = 'Country must be at least 2 characters';
        }
        
        if (phoneNumber.trim().length < 10) {
            newErrors.phoneNumber = 'Phone number must be at least 10 characters';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    // Debounced username availability check
    useEffect(() => {
        // Clear any existing timeout
        if (usernameCheckTimeout.current) {
            clearTimeout(usernameCheckTimeout.current);
        }

        if (username.trim().length >= 3) {
            // Set new timeout for 500ms delay
            usernameCheckTimeout.current = setTimeout(() => {
                checkUsernameAvailability(username.trim());
            }, 500);
        } else {
            // Reset status if username is too short
            setUsernameStatus({ checking: false, available: null, message: '' });
        }

        // Cleanup timeout on unmount or username change - reads the ref's
        // live value at cleanup time, unlike a state snapshot, so a pending
        // timeout is always cancelled even right after being scheduled.
        return () => {
            if (usernameCheckTimeout.current) {
                clearTimeout(usernameCheckTimeout.current);
            }
        };
    }, [username]);
    
    const checkUsernameAvailability = async (usernameToCheck) => {
        setUsernameStatus({ checking: true, available: null, message: 'Checking...' });
        
        try {
            const result = await apiClient.get('/api/users/CheckUsernameAvailability?UserName=' + encodeURIComponent(usernameToCheck));
            console.log('Username availability result:', result);

            if (result === 'Available') {
                setUsernameStatus({
                    checking: false,
                    available: true,
                    message: 'Username is available!'
                });
            } else if (result === 'Already Taken') {
                setUsernameStatus({
                    checking: false,
                    available: false,
                    message: 'Username is already taken'
                });
            } else {
                setUsernameStatus({
                    checking: false,
                    available: null,
                    message: 'Unable to check availability'
                });
            }
        } catch (error) {
            console.error('Username availability check error:', error);
            setUsernameStatus({ 
                checking: false, 
                available: null, 
                message: 'Error checking availability' 
            });
        }
    };
    
    const HandleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const data = await apiClient.post('/api/users', {
                first_name,
                last_name,
                UserName: username,
                companyName,
                address1,
                address2,
                city,
                postcode,
                country,
                email,
                password,
                phoneNumber,
                role_type: 'standard'
            });

            console.log("Registration data received:", data);
            setRegistrationData({
                token: data.token,
                user: {
                    email: email,
                    first_name: first_name,
                    last_name: last_name,
                    role_type: "standard"
                }
            });

            // Store temporary token for 2FA setup
            localStorage.setItem('accessToken', data.token);

            // Show mandatory 2FA setup
            setShowMandatory2FA(true);
        } catch (error) {
            console.error('Error:', error);
            setErrors({ general: 'Registration failed. Please try again.' });
        }
}

    const handle2FASuccess = (method) => {
        console.log('Mandatory 2FA setup successful with method:', method);
        
        // Complete registration and login
        if (registrationData) {
            Globals.UserName = registrationData.user.email;
            Globals.UserType = registrationData.user.role_type;
            Globals.FirstName = registrationData.user.first_name;
            Globals.LastName = registrationData.user.last_name;
            Globals.LoginTime = new Date();
            
            // Mark 2FA as completed for this user
            localStorage.setItem('2faEnabled', method);
            localStorage.setItem('2faCompleted', 'true');
            
            // Session expiry is now handled by authFetch's silent refresh,
            // not a fixed client-side timer - see index.js.
            simpleSessionTimer.startSession();

            navigate('/home');
        }
        setShowMandatory2FA(false);
    };
    
    const handle2FACancel = () => {
        // For mandatory 2FA, we don't allow canceling
        // User must complete 2FA setup to proceed
        alert('Two-factor authentication setup is required to complete registration. Please choose a 2FA method to continue.');
    };
    
    if (showMandatory2FA) {
        return (
            <TwoFactorAuth 
                mode="setup"
                onSuccess={handle2FASuccess}
                onCancel={handle2FACancel}
            />
        );
    }

  return (

    <div style={{ display: 'flex', minHeight: '100vh', width: '100vw', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 0'}} className="NotTheBody">
    <div className='wrapper'>
        <form onSubmit={HandleSubmit}>
            <h1>Register</h1>
            <div className="input-box">
                <input type='text' placeholder="First Name" value={first_name} onChange={(e) => { setFirstName(e.target.value) }} required/>
                {errors.first_name && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.first_name}</p>}
            </div>
            <div className="input-box">
                <input type='text' placeholder="Last Name" value={last_name} onChange={(e) => { setLastName(e.target.value) }} required/>
                {errors.last_name && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.last_name}</p>}
            </div>
            <div className="input-box">
                <input type='email' placeholder="Email" value={email} onChange={(e) => { setEmail(e.target.value) }} required/>
            </div>
            <div className="input-box">
                <input type='text' placeholder="Company Name" value={companyName} onChange={(e) => { setCompanyName(e.target.value) }} required/>
                {errors.companyName && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.companyName}</p>}
            </div>
            <div className="input-box">
                <input type='text' placeholder="Address Line 1" value={address1} onChange={(e) => { setAddress1(e.target.value) }} required/>
                {errors.address1 && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.address1}</p>}
            </div>
            <div className="input-box">
                <input type='text' placeholder="Address Line 2 (Optional)" value={address2} onChange={(e) => { setAddress2(e.target.value) }}/>
            </div>
            <div className="input-box">
                <input type='text' placeholder="City" value={city} onChange={(e) => { setCity(e.target.value) }} required/>
                {errors.city && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.city}</p>}
            </div>
            <div className="input-box">
                <input type='text' placeholder="Postcode" value={postcode} onChange={(e) => { setPostcode(e.target.value) }} required/>
                {errors.postcode && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.postcode}</p>}
            </div>
            <div className="input-box">
                <input type='text' placeholder="Country" value={country} onChange={(e) => { setCountry(e.target.value) }} required/>
                {errors.country && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.country}</p>}
            </div>
            <div className="input-box">
                <input type='tel' placeholder="Phone Number" value={phoneNumber} onChange={(e) => { setPhoneNumber(e.target.value) }} required/>
                {errors.phoneNumber && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.phoneNumber}</p>}
            </div>
            <div className="input-box">
                <input 
                    type='text' 
                    placeholder="Username" 
                    value={username} 
                    onChange={(e) => { setUsername(e.target.value) }} 
                    required
                    style={{
                        borderColor: usernameStatus.available === true ? '#28a745' : 
                                   usernameStatus.available === false ? '#dc3545' : 
                                   usernameStatus.checking ? '#ffc107' : ''
                    }}
                />
                {/* Username availability status */}
                {username.length >= 3 && (
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginTop: '5px',
                        fontSize: '12px'
                    }}>
                        {usernameStatus.checking && (
                            <>
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid #f3f3f3',
                                    borderTop: '2px solid #ffc107',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                                <span style={{ color: '#ffc107' }}>Checking availability...</span>
                            </>
                        )}
                        {!usernameStatus.checking && usernameStatus.available === true && (
                            <>
                                <span style={{ color: '#28a745' }}>✓</span>
                                <span style={{ color: '#28a745' }}>Username is available!</span>
                            </>
                        )}
                        {!usernameStatus.checking && usernameStatus.available === false && (
                            <>
                                <span style={{ color: '#dc3545' }}>✗</span>
                                <span style={{ color: '#dc3545' }}>Username is already taken</span>
                            </>
                        )}
                        {!usernameStatus.checking && usernameStatus.available === null && usernameStatus.message && (
                            <>
                                <span style={{ color: '#6c757d' }}>⚠</span>
                                <span style={{ color: '#6c757d' }}>{usernameStatus.message}</span>
                            </>
                        )}
                    </div>
                )}
                {errors.username && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.username}</p>}
            </div>
            <div className="input-box">
                <input type="password" placeholder="Password" value={password} onChange={(e) => { setPassword(e.target.value) }} required/>
                <PasswordStrengthIndicator password={password} onStrengthChange={setPasswordStrength} />
                {errors.password && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.password}</p>}
            </div>
            <div className="input-box">
                <input type="password" placeholder="Confirm Password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value) }} required/>
                {errors.confirmPassword && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.confirmPassword}</p>}
            </div>
            {errors.general && <p style={{color: 'red', fontSize: '12px', margin: '5px 0 0 0'}}>{errors.general}</p>}
            <button type='submit'>Register & Setup 2FA</button>
            <br/><br/>
            <p>Already have an account? <a href="/login">Login</a></p>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '15px' }}>
                📱 Two-factor authentication setup is required for all new accounts
            </p>
        </form>
    </div>
    </div>
  )
}

export default RegisterForm